import React, { useState } from 'react';
import styled from 'styled-components';
import { XIcon, SparklesIcon, InformationCircleIcon } from '@heroicons/react/outline';
import { LoadingOutlined } from '@ant-design/icons';
import { colors, spacing } from '../../design-system';
import { message as antMessage, Tooltip } from 'antd';
import BoringAvatar from 'boring-avatars';
import type { Creator } from '../Creators/creatorTypes';

interface CompanionDraftSidebarProps {
  isOpen: boolean;
  creator: Creator | null;
  onClose: () => void;
  companionName?: string;
  companionId?: string;
  companionColor?: string;
  generatedDraft?: string; // The AI-generated draft to display
  messages?: Array<{
    message_id: string;
    user_type: 'USER' | 'CREATOR';
    sender: string;
    subject?: string;
    received_at: string;
    content: string;
    has_attachments?: boolean;
    is_target?: boolean;
  }>; // Messages from the selected thread (response.messages)
  conversationPreview?: Array<{
    message_id: string;
    user_type: 'USER' | 'CREATOR';
    sender: string;
    subject?: string;
    received_date_time?: string;
    content: string;
    has_attachments?: boolean;
  }>; // Global conversation preview (response.conversation_preview) - for future use
  onRegenerateDraft?: (feedback: string) => Promise<void>; // Callback to regenerate with feedback
}

const DrawerContainer = styled.div<{ $isOpen: boolean }>`
  position: relative;
  height: 100%;
  width: ${props => props.$isOpen ? '35%' : '0%'};
  min-width: ${props => props.$isOpen ? '480px' : '0px'};
  max-width: 620px;
  background: white;
  border-left: 1px solid ${colors.border.secondary};
  overflow: hidden;
  z-index: 10;
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: white;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  min-height: 38px;
`;

const DrawerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const TitleText = styled.h3`
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  color: ${colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 3px;
  transition: all 0.12s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #1a1a1a;
    background: rgba(0, 0, 0, 0.02);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 180px; /* Space for floating composer */
  background: #fbfcfd;
  
  /* Hide scrollbar completely like MessageList */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
`;

const Section = styled.div`
  padding: 20px 20px;
  border-bottom: 1px solid ${colors.border.secondary};
  background: white;
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  svg {
    width: 14px;
    height: 14px;
    opacity: 0.6;
  }
`;

const ConversationContainer = styled.div`
  background: ${colors.background.secondary};
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.border.secondary};
`;

const ThreadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const MessageCard = styled.div<{ $userType?: string }>`
  padding: 12px 0;
  position: relative;
  border-bottom: 1px solid ${colors.border.secondary};
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:first-child {
    padding-top: 0;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const MessageAvatarContainer = styled.div`
  flex-shrink: 0;
  margin-top: 0;
`;

const SquaredAvatar = styled.div<{ $userType: string; $hasImage?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  background: ${props => props.$hasImage ? 'transparent' : (props.$userType === 'CREATOR' ? colors.palette.purple[50] : colors.palette.blue[50])};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: ${props => props.$userType === 'CREATOR' ? colors.palette.purple[600] : colors.palette.blue[600]};
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MessageContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const SenderName = styled.div`
  font-weight: 500;
  font-size: 11px;
  color: ${colors.text.primary};
  line-height: 1.4;
`;

const MessageTimestamp = styled.div`
  font-size: 10px;
  color: ${colors.text.tertiary};
  opacity: 0.7;
  flex-shrink: 0;
  margin-left: 8px;
`;

const MessageBody = styled.div`
  color: ${colors.text.secondary};
  line-height: 1.5;
  font-size: 11px;
  word-wrap: break-word;
  white-space: pre-wrap;
`;

// Composer Area - EXACT copy from MessageComposer
const ComposeArea = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  z-index: 100;
  background: #fbfcfd;
`;

const ComposeContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

// TipTap Editor Wrapper - EXACT copy
const TiptapEditorWrapper = styled.div`
  min-height: 260px;
  padding: 6px 8px;
  background: transparent;
  cursor: text;
  position: relative;
  
  textarea {
    outline: none;
    font-size: 12px;
    line-height: 1.4;
    color: ${colors.text.primary};
    min-height: 260px;
    width: 100%;
    border: none;
    background: transparent;
    font-family: inherit;
    resize: none;
    
    &::placeholder {
      color: ${colors.text.disabled};
    }
    
    &:focus {
      outline: none;
    }
  }
`;

const ComposeFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  background: transparent;
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NoteInput = styled.input`
  background: none;
  border: none;
  color: ${colors.text.tertiary};
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  font-size: 12px;
  font-weight: 400;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.15s ease;
  opacity: 0.7;
  min-width: 200px;
  
  &:hover {
    color: ${colors.text.secondary};
    background: ${colors.background.secondary};
    opacity: 1;
  }
  
  &:focus {
    outline: none;
    color: ${colors.text.secondary};
    background: ${colors.background.secondary};
    opacity: 1;
  }
  
  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 1px solid ${colors.border.primary};
  border-radius: 6px;
  color: ${colors.text.secondary};
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  
  &:hover {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const RegenerateButton = styled.button<{ $hasNote?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: ${props => props.$hasNote ? colors.brand.primarySurface : 'transparent'};
  border: 1px solid ${props => props.$hasNote ? colors.brand.primary : colors.border.primary};
  border-radius: 6px;
  color: ${props => props.$hasNote ? colors.brand.primary : colors.text.secondary};
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  
  &:hover {
    background: ${props => props.$hasNote ? colors.brand.primarySurface : colors.background.hover};
    color: ${props => props.$hasNote ? colors.brand.primary : colors.text.primary};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SendButton = styled.button<{ $disabled?: boolean }>`
  background: ${(props) =>
    props.$disabled
      ? colors.background.secondary
      : colors.background.secondary};
  color: ${(props) =>
    props.$disabled ? colors.text.disabled : colors.text.secondary};
  border: 1px solid
    ${(props) =>
      props.$disabled ? colors.border.secondary : colors.border.primary};
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 400;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;

  &:hover:not(:disabled) {
    background: ${colors.background.primary};
    color: ${colors.text.primary};
    border-color: ${colors.border.primary};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: ${colors.text.tertiary};
  font-size: 12px;
  padding: 40px 20px;
  opacity: 0.6;
`;

export const CompanionDraftSidebar: React.FC<CompanionDraftSidebarProps> = ({
  isOpen,
  creator,
  onClose,
  companionName = 'Campaign Carl',
  companionId = 'campaign-carl',
  companionColor = '#3b82f6',
  generatedDraft,
  messages = [],
  conversationPreview = [],
  onRegenerateDraft
}) => {
  const [draftMessage, setDraftMessage] = useState('');
  const [note, setNote] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  // Update draft message when generatedDraft changes
  React.useEffect(() => {
    if (generatedDraft) {
      setDraftMessage(generatedDraft);
    }
  }, [generatedDraft]);

  // Get creator display name and handle
  const getCreatorInfo = () => {
    if (!creator) return { name: 'Creator', handle: '', platform: '' };
    
    // Try TikTok first
    if (creator.tiktok_data && creator.tiktok_data.length > 0) {
      return {
        name: creator.tiktok_data[0].handle,
        handle: `@${creator.tiktok_data[0].handle}`,
        platform: 'tiktok',
        profileImage: creator.tiktok_data[0].profile_image_url
      };
    }
    
    // Try Instagram
    if (creator.instagram_data && creator.instagram_data.length > 0) {
      return {
        name: creator.instagram_data[0].handle,
        handle: `@${creator.instagram_data[0].handle}`,
        platform: 'instagram',
        profileImage: creator.instagram_data[0].profile_image_url
      };
    }
    
    // Try Twitch
    if (creator.twitch_data && creator.twitch_data.length > 0) {
      return {
        name: creator.twitch_data[0].handle,
        handle: `@${creator.twitch_data[0].handle}`,
        platform: 'twitch',
        profileImage: creator.twitch_data[0].profile_image_url
      };
    }
    
    return {
      name: creator.creator_shipping?.name || 'Creator',
      handle: '',
      platform: ''
    };
  };

  const creatorInfo = getCreatorInfo();

  // Use messages from the selected thread (response.messages), not conversation_preview
  const displayMessages = messages.length > 0 ? messages.map(msg => ({
    id: msg.message_id,
    sender: msg.sender,
    userType: msg.user_type,
    timestamp: msg.received_at || new Date().toISOString(),
    content: msg.content,
    subject: msg.subject,
    isTarget: msg.is_target // Highlight the message that AI selected as source
  })) : [];

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSaveDraft = () => {
    antMessage.success('Draft saved');
    onClose();
  };

  const handleRegenerate = async () => {
    if (!onRegenerateDraft) {
      antMessage.info('Regeneration not available');
      return;
    }
    
    setIsRegenerating(true);
    try {
      const feedback = note.trim() || 'Please regenerate this draft with improvements';
      await onRegenerateDraft(feedback);
      setNote(''); // Clear the note after successful regeneration
      antMessage.success('Draft regenerated!');
    } catch (error) {
      console.error('Failed to regenerate draft:', error);
      antMessage.error('Failed to regenerate draft');
    } finally {
      setIsRegenerating(false);
    }
  };

  if (!creator) return null;

  return (
    <DrawerContainer $isOpen={isOpen}>
      <DrawerHeader>
        <DrawerTitle>
          <CloseButton onClick={onClose}>
            <XIcon />
          </CloseButton>
          <HeaderAvatar>
            <BoringAvatar
              size={24}
              name={companionId}
              variant="beam"
              colors={[companionColor, `${companionColor}dd`, `${companionColor}bb`, `${companionColor}99`, '#f3f4f6']}
            />
          </HeaderAvatar>
          <TitleText>
            {creatorInfo.handle || creatorInfo.name}
          </TitleText>
        </DrawerTitle>
      </DrawerHeader>

      <DrawerContent>
        {/* Conversation History - now full width */}
        <ConversationContainer>
          <SectionTitle style={{ marginBottom: '12px' }}>
            Previous Conversation
            {displayMessages.length > 0 && (
              <span style={{ 
                fontSize: '10px', 
                fontWeight: 400, 
                marginLeft: '8px', 
                opacity: 0.7 
              }}>
                ({displayMessages.length} recent messages)
              </span>
            )}
          </SectionTitle>
          {displayMessages.length > 0 ? (
            <ThreadContainer>
              {displayMessages.map((message) => (
                <MessageCard key={message.id} $userType={message.userType}>
                  <MessageWrapper>
                    <MessageAvatarContainer>
                      <SquaredAvatar 
                        $userType={message.userType}
                        $hasImage={message.userType === 'CREATOR' && !!creatorInfo.profileImage}
                      >
                        {message.userType === 'CREATOR' && creatorInfo.profileImage ? (
                          <AvatarImage 
                            src={creatorInfo.profileImage} 
                            alt={creatorInfo.name}
                          />
                        ) : (
                          message.userType === 'CREATOR' ? 'C' : 'U'
                        )}
                      </SquaredAvatar>
                    </MessageAvatarContainer>
                    
                    <MessageContentWrapper>
                      <MessageHeader>
                        <SenderName>
                          {message.sender}
                          {message.subject && (
                            <span style={{ 
                              fontSize: '10px', 
                              fontWeight: 400, 
                              marginLeft: '6px', 
                              opacity: 0.6 
                            }}>
                              • {message.subject}
                            </span>
                          )}
                        </SenderName>
                        <MessageTimestamp>
                          {formatTimestamp(message.timestamp)}
                        </MessageTimestamp>
                      </MessageHeader>
                      <MessageBody>{message.content}</MessageBody>
                    </MessageContentWrapper>
                  </MessageWrapper>
                </MessageCard>
              ))}
            </ThreadContainer>
          ) : (
            <EmptyState>No previous messages found</EmptyState>
          )}
        </ConversationContainer>
      </DrawerContent>

      {/* Composer Area - matches MessageComposer */}
      <ComposeArea>
        <ComposeContainer>
          <TiptapEditorWrapper>
            <textarea
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              placeholder="Reply to email conversation..."
            />
          </TiptapEditorWrapper>

          <ComposeFooter>
            <FooterLeft>
              <Tooltip
                title={
                  <div style={{ padding: '4px 0' }}>
                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>{companionName}'s Analysis</div>
                    <div style={{ fontSize: '12px', lineHeight: 1.6 }}>
                      <div style={{ marginBottom: '4px' }}>• Missing: Rate, Shipping Address</div>
                      <div style={{ marginBottom: '4px' }}>• Last contacted: 2 days ago</div>
                      <div>• Recommendation: Follow up asking for specifics</div>
                    </div>
                  </div>
                }
                placement="topLeft"
                overlayStyle={{ maxWidth: '320px' }}
                overlayInnerStyle={{
                  backgroundColor: 'white',
                  color: colors.text.primary,
                  padding: '12px 14px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                }}
                color="white"
              >
                <InfoButton>
                  <InformationCircleIcon />
                </InfoButton>
              </Tooltip>
              
              <Tooltip
                title={isRegenerating ? "Regenerating..." : (note ? `Regenerate with note: "${note}"` : "Regenerate draft")}
                placement="top"
              >
                <RegenerateButton 
                  onClick={handleRegenerate} 
                  $hasNote={!!note}
                  disabled={isRegenerating}
                  style={{ cursor: isRegenerating ? 'wait' : 'pointer' }}
                >
                  {isRegenerating ? (
                    <LoadingOutlined style={{ width: '16px', height: '16px' }} />
                  ) : (
                    <SparklesIcon style={{ width: '16px', height: '16px' }} />
                  )}
                </RegenerateButton>
              </Tooltip>
              
              <NoteInput
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={`Add note for ${companionName}...`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleRegenerate();
                  }
                }}
              />
            </FooterLeft>
            
            <FooterRight>
              <SendButton onClick={handleSaveDraft} $disabled={!draftMessage.trim()}>
                Send
              </SendButton>
            </FooterRight>
          </ComposeFooter>
        </ComposeContainer>
      </ComposeArea>
    </DrawerContainer>
  );
};

